import { API_ENDPOINTS } from "../config/constants";
import { isFullOfflineModeEnabled, subscribeToFullOfflineMode } from "./fullOfflineMode";
import { isElectronOfflineRuntime } from "../offline/runtime";
import { enqueueMutation, listMutations } from "../offline/mutationQueue";
import { runMutationSync } from "../offline/syncEngine";
import { resolveOfflineOrderCode } from "../offline/orderCode";

const SYNC_INTERVAL_MS = 30_000;
let syncTimer = null;

const isDesktop = isElectronOfflineRuntime;

// SQLite CURRENT_TIMESTAMP is UTC but has no trailing Z. Normalize it before
// comparing with a POS user's locally selected calendar date.
const parseStoredUtcDate = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return null;
  return new Date(/^\d{4}-\d{2}-\d{2}\s/.test(raw) ? `${raw.replace(" ", "T")}Z` : raw);
};

const localDateKey = (value) => {
  const date = parseStoredUtcDate(value);
  if (!date || Number.isNaN(date.getTime())) return String(value || "").slice(0, 10);
  const fields = new Intl.DateTimeFormat("en", {
    timeZone: "Asia/Kolkata", year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(date).reduce((result, part) => ({ ...result, [part.type]: part.value }), {});
  return `${fields.year}-${fields.month}-${fields.day}`;
};

const normalizedTimestamp = (value) => {
  const date = parseStoredUtcDate(value);
  return date && !Number.isNaN(date.getTime()) ? date.toISOString() : value;
};

const newId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `desktop-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const publishQueueChange = (detail = {}) => {
  window.dispatchEvent(new CustomEvent("desktop-order-sync-change", { detail }));
};

export const enqueuePendingOrder = async ({
  restaurantId,
  payload,
  localOrder = null,
  localItems = [],
  operation = "create",
  returnDetails = false,
}) => {
  if (!isDesktop()) return null;
  const isEdit = operation === "edit";
  const existingOrderId = String(payload?.orderid || payload?.id || "").trim();
  if (isEdit && !existingOrderId) {
    throw new Error("An order id is required to queue an offline edit.");
  }
  // A stable edit id means repeated offline saves replace the older queued
  // edit instead of replaying several stale versions of the same order.
  const id = isEdit
    ? `edit-${restaurantId}-${existingOrderId}`
    : newId();
  const endpoint = isEdit ? API_ENDPOINTS.EDIT_ORDER : API_ENDPOINTS.CREATE_ORDER;
  const localOnly = isFullOfflineModeEnabled();
  const enqueueResult = await enqueueMutation({
    id,
    restaurantId,
    entityType: "order",
    action: isEdit ? "update" : "create",
    endpoint,
    payload,
    metadata: { localOnly },
    entityKey: isEdit ? existingOrderId : `pending-${id}`,
  });

  const orderCode = String(
    localOrder?.ordercode ||
    localOrder?.order_code ||
    enqueueResult?.orderCode ||
    existingOrderId ||
    `OFFLINE-${id.slice(0, 8)}`
  );
  const localOrderId = isEdit ? existingOrderId : `pending-${id}`;
  const cachedOrder = localOrder ? {
    ...localOrder,
    id: localOrderId,
    orderid: localOrderId,
    ordercode: orderCode,
    ...(isEdit ? { is_pending_edit: !localOnly } : { is_pending_sync: !localOnly }),
    ...(localOnly ? { is_local_only: true } : {}),
  } : null;

  // Edits overwrite the existing SQLite list row, so Dashboard and reports
  // see the new values now. New orders are projected from the queue itself.
  if (isEdit && cachedOrder && window.desktopOrders) {
    await window.desktopOrders.upsert({ restaurantId, orders: [cachedOrder] });
  }
  if (window.desktopOrderDetails && localOrder) {
    await window.desktopOrderDetails.upsert({
      restaurantId,
      keys: [localOrderId, orderCode],
      response: {
        success: true,
        offline: true,
        pending_operation: isEdit ? "edit" : "create",
        orderdetails: cachedOrder,
        itemslist: localItems,
      },
    });
  }
  publishQueueChange({ type: "queued", operation: isEdit ? "edit" : "create", id, restaurantId });
  return returnDetails
    ? { id, orderCode, localOrderId, localOnly }
    : id;
};

export const getPendingOrderCount = async (restaurantId) => {
  if (!isDesktop()) return 0;
  const rows = await listMutations({ entityType: "order", restaurantId, includeDeferred: true });
  return Array.isArray(rows)
    ? rows.filter((item) => item.metadata?.localOnly !== true).length
    : 0;
};

export const getOrderSyncSummary = async (restaurantId) => {
  if (!isDesktop()) return null;
  const rows = await listMutations({
    entityType: "order",
    restaurantId,
    includeDeferred: true,
    includeFailed: true,
  });
  const items = (Array.isArray(rows) ? rows : [])
    .filter((item) => item.metadata?.localOnly !== true);
  const failed = items.filter((item) => item.status === "failed").length;
  return { pending: items.length - failed, failed, total: items.length };
};

const pendingOrderToDashboardItem = (item) => ({
  id: `pending-${item.id}`,
  ordercode: resolveOfflineOrderCode(item),
  created_at: normalizedTimestamp(item.createdAt),
  tableid: item.payload?.tableid || 0,
  totalamount: item.payload?.totalPrice || item.payload?.totalamount || 0,
  totalPrice: item.payload?.totalPrice || item.payload?.totalamount || 0,
  payment_mode: item.payload?.payment_mode || item.payload?.mode || "Cash",
  mode: item.payload?.mode || item.payload?.payment_mode || "Cash",
  partyid: item.payload?.partyid ?? item.payload?.party_id ?? null,
  party_id: item.payload?.party_id ?? item.payload?.partyid ?? null,
  itemlist: item.payload?.itemlist || [],
  is_pending_sync: item.metadata?.localOnly !== true,
  is_local_only: item.metadata?.localOnly === true,
});

export const getPendingOrdersForDate = async (restaurantId, date) => {
  if (!isDesktop() || !restaurantId) return [];
  const rows = await listMutations({ entityType: "order", restaurantId, includeDeferred: true });
  const selectedDate = String(date || "").slice(0, 10);
  return (Array.isArray(rows) ? rows : [])
    .filter((item) => item.endpoint === API_ENDPOINTS.CREATE_ORDER)
    .filter((item) => !selectedDate || localDateKey(item.createdAt) === selectedDate)
    .map(pendingOrderToDashboardItem);
};

const isPermanentServerFailure = (response) => response.status >= 400 && response.status < 500;

export const syncPendingOrders = async ({ retryFailed = false } = {}) => {
  return runMutationSync({
    key: "orders",
    entityTypes: ["order"],
    includeFailed: retryFailed,
    execute: async (item) => {
      const response = await fetch(item.endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Idempotency-Key": item.id,
        },
        body: JSON.stringify(item.payload),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || result?.success === false) {
        const error = new Error(result?.message || `Server returned ${response.status}`);
        error.permanent = isPermanentServerFailure(response);
        throw error;
      }
      return result;
    },
    onSuccess: (item) => {
      publishQueueChange({ type: "synced", id: item.id, restaurantId: item.restaurantId });
    },
  });
};

export const startOrderSyncService = () => {
  if (!isDesktop() || syncTimer) return;
  const run = () => syncPendingOrders().catch(() => {});
  window.addEventListener("online", run);
  subscribeToFullOfflineMode((offlineEnabled) => {
    if (!offlineEnabled) run();
  });
  syncTimer = window.setInterval(run, SYNC_INTERVAL_MS);
  run();
};
